import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAbility } from '@casl/react';
import { Container, Plus, Search, Truck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Can } from '../../components/Can';
import AccessRestricted from '../../components/AccessRestricted';
import { AbilityContext } from '../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import { ImportOrder, ImportStatus, Yard } from '../../types/stock.types';
import { importsApi, yardsApi } from '../../utils/stock-api';
import { cn } from '../../components/ui/utils';

const ALL = 'all';

const STATUS_FILTERS = [
    { value: ALL, label: 'All statuses' },
    { value: 'open', label: 'Open' },
    { value: 'receiving', label: 'Receiving' },
    { value: 'closed', label: 'Closed' },
    { value: 'voided', label: 'Voided' },
];

const STATUS_STYLES: Record<ImportStatus, string> = {
    open: 'text-slate-700 bg-slate-100',
    receiving: 'text-blue-700 bg-blue-50',
    closed: 'text-emerald-800 bg-emerald-100 ring-1 ring-emerald-200',
    voided: 'text-red-700 bg-red-50 ring-1 ring-red-200',
};

export const ImportStatusPill: React.FC<{ status: ImportStatus }> = ({ status }) => (
    <span className={cn(
        'inline-flex items-center text-xs font-medium px-2 py-1 rounded-md whitespace-nowrap capitalize',
        STATUS_STYLES[status] ?? 'text-slate-700 bg-slate-100'
    )}>
        {status}
    </span>
);

/**
 * Import Orders list — STOCK.md §6, routed per yard (STOCK-MENU.md §2).
 *
 * The receipt counts matter more than the line counts here: an import order's progress is
 * "3 containers arrived, 2 counted and signed", not "how many products were ordered".
 */
export default function ImportOrdersPage() {
    const ability = useAbility(AbilityContext);
    const canRead = ability.can(ACTIONS.READ, RESOURCES.STOCK_IMPORT_ORDER);
    const navigate = useNavigate();
    const { yardId: yardIdParam } = useParams<{ yardId?: string }>();
    const scopedYardId = yardIdParam ? Number(yardIdParam) : null;

    const [orders, setOrders] = useState<ImportOrder[]>([]);
    const [yards, setYards] = useState<Yard[]>([]);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState<string>(ALL);
    const [search, setSearch] = useState('');

    const fetchOrders = useCallback(async () => {
        try {
            setLoading(true);
            setOrders(await importsApi.list({
                status: status === ALL ? undefined : status,
                yardId: scopedYardId ? String(scopedYardId) : undefined,
            }));
        } catch (err: any) {
            toast.error(err?.message || 'Failed to load import orders');
        } finally {
            setLoading(false);
        }
    }, [status, scopedYardId]);

    useEffect(() => { if (canRead) fetchOrders(); }, [canRead, fetchOrders]);

    useEffect(() => {
        if (!canRead) return;
        yardsApi.list({ status: 'open' }).then(setYards).catch(() => undefined);
    }, [canRead]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return orders;
        return orders.filter((o) =>
            [o.orderNumber, o.externalRef, o.vendor?.tradingName, o.yard?.yardCode]
                .filter(Boolean)
                .some((v) => (v as string).toLowerCase().includes(q))
        );
    }, [orders, search]);

    if (!canRead) return <AccessRestricted />;

    const scopedYard = scopedYardId ? yards.find((y) => y.id === scopedYardId) ?? null : null;
    const detailHref = (id: number) =>
        scopedYardId ? `/stock/yards/${scopedYardId}/imports/${id}` : `/stock/imports/${id}`;
    // The create form is a full page with its own URL, like every other form in Idarah.
    // Scope it only when the yard is actually open — from a closed yard's list (§6)
    // the unscoped form is the honest destination.
    const newHref = scopedYard ? `/stock/yards/${scopedYard.id}/imports/new` : '/stock/imports/new';

    return (
        <div className="space-y-4 md:space-y-6 p-2 md:p-0">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold">Import Orders</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        {scopedYard
                            ? `${scopedYard.yardName} · material arriving from vendors.`
                            : 'Material arriving into a yard from a vendor, by container.'}
                    </p>
                </div>
                <Can I={ACTIONS.CREATE} a={RESOURCES.STOCK_IMPORT_ORDER}>
                    <Button
                        onClick={() => navigate(newHref)}
                        className="flex items-center gap-2 w-full md:w-auto bg-slate-900 hover:bg-slate-800 text-white border-0"
                    >
                        <Plus size={16} /><span>New Import Order</span>
                    </Button>
                </Can>
            </div>

            <div className="flex flex-col md:flex-row items-start md:items-center w-full gap-4 flex-wrap">
                <div className="relative w-full md:flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search by order number, vendor or reference..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10 w-full"
                    />
                </div>
                <div className="w-full md:w-[190px]">
                    <Select value={status} onValueChange={setStatus}>
                        <SelectTrigger className="w-full bg-white"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {STATUS_FILTERS.map((s) => (
                                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <span className="text-sm text-muted-foreground">{filtered.length} records</span>
            </div>

            {loading ? (
                <div className="space-y-3 animate-pulse">
                    {[0, 1, 2].map((i) => <div key={i} className="h-14 bg-gray-300 rounded-md" />)}
                </div>
            ) : filtered.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">No import orders found</div>
            ) : (
                <>
                    <div className="md:hidden space-y-3">
                        {filtered.map((o) => (
                            <Card key={o.id} className="p-4 active:bg-accent/40"
                                  onClick={() => navigate(detailHref(o.id))}>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <span className="font-mono font-medium">{o.orderNumber}</span>
                                        <p className="text-xs text-muted-foreground mt-1 truncate">
                                            {o.vendor?.tradingName ?? '–'}
                                        </p>
                                    </div>
                                    <ImportStatusPill status={o.status} />
                                </div>
                                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                                    <span className="flex items-center gap-1.5">
                                        <Container size={14} />
                                        {o.receivedCount ?? 0}/{o.receiptCount ?? 0} received
                                    </span>
                                    <span>Expected {o.expectedDate ?? '–'}</span>
                                </div>
                            </Card>
                        ))}
                    </div>

                    <Card className="hidden md:block">
                        <CardContent className="p-3">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="font-semibold text-slate-900">Order Number</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Vendor</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Yard</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Expected</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Receipts</TableHead>
                                        <TableHead className="font-semibold text-slate-900">External Ref</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Status</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filtered.map((o) => (
                                        <TableRow key={o.id} onClick={() => navigate(detailHref(o.id))}
                                                  className="cursor-pointer">
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <Truck size={16} className="text-muted-foreground" />
                                                    <span className="font-mono">{o.orderNumber}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell>{o.vendor?.tradingName ?? '–'}</TableCell>
                                            <TableCell className="font-mono text-muted-foreground">
                                                {o.yard?.yardCode ?? '–'}
                                            </TableCell>
                                            <TableCell>{o.expectedDate ?? '–'}</TableCell>
                                            <TableCell>{o.receivedCount ?? 0}/{o.receiptCount ?? 0}</TableCell>
                                            <TableCell className="font-mono text-sm">
                                                {o.externalRef ?? <span className="text-muted-foreground">–</span>}
                                            </TableCell>
                                            <TableCell><ImportStatusPill status={o.status} /></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
    );
}
